<template>
  <div class="flex flex-col gap-3 overflow-hidden" style="height: calc(100vh - 165px);">
    <Worklist_FilterBar
      storage-key="risk_stratification_filters"
      :demographic-filters="[]"
      @filter-changed="onEntityFilterChanged"
    />

    <div class="grid grid-cols-12 gap-3 shrink-0 transition-opacity" :class="loading ? 'opacity-40 pointer-events-none' : ''">
      <div class="col-span-4 rounded border border-gray-200 bg-white px-4 py-3 flex flex-col">
        <div class="flex items-center justify-between">
          <h3 class="text-xs font-semibold text-gray-800">Risk Tranche <span v-if="loading" class="ml-1 text-[10px] font-normal text-gray-400">Loading…</span></h3>
          <button v-if="activeTranche || activeCategory" class="text-[11px] text-blue-600 hover:underline" @click="clearSelection">Clear</button>
        </div>
        <div class="flex items-center gap-3 flex-1 min-h-0">
          <div class="w-[190px] shrink-0">
            <Pie_Chart
              :key="pieKey"
              :chart_data="pieData"
              :chart_config="pieConfig"
              :hide-export="true"
              override-height="h-[190px]"
              @slice-click="onSliceClick"
            />
          </div>
          <ul class="flex-1 min-w-0 space-y-1.5">
            <li
              v-for="t in trancheOrder"
              :key="t"
              class="flex items-center gap-2 text-[11px] cursor-pointer rounded px-1.5 py-1"
              :class="activeTranche === t ? 'bg-blue-50 text-blue-900' : 'text-gray-700 hover:bg-gray-50'"
              @click="toggleTranche(t)"
            >
              <span class="inline-block w-2.5 h-2.5 rounded-sm shrink-0" :style="{ backgroundColor: trancheStyle(t).hex }" />
              <span class="flex-1 truncate">{{ trancheShort(t) }}</span>
              <span class="tabular-nums font-semibold text-gray-800">{{ (counts[t] || 0).toLocaleString() }}</span>
              <span class="tabular-nums text-gray-400 w-9 text-right">{{ pct(counts[t]) }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="col-span-8 rounded border border-gray-200 bg-white px-4 py-3">
        <h3 class="text-xs font-semibold text-gray-800 mb-2">Patients by Risk Tranche and Intervention Category <span v-if="loading" class="ml-1 text-[10px] font-normal text-gray-400">Loading…</span></h3>
        <table class="w-full table-fixed text-[11px]">
          <colgroup>
            <col style="width: 150px" />
            <col v-for="c in matrixCategories" :key="c" />
            <col style="width: 64px" />
          </colgroup>
          <thead>
            <tr class="align-bottom">
              <th class="text-left font-medium text-gray-500 pb-1 pr-2"></th>
              <th
                v-for="c in matrixCategories"
                :key="c"
                class="text-right font-medium pb-1 px-1 leading-tight cursor-pointer select-none text-[10px]"
                :class="activeCategory === c ? 'text-blue-700' : 'text-gray-500 hover:text-gray-800'"
                @click="toggleCategory(c)"
              >{{ categoryLabel(c) }}</th>
              <th class="text-right font-semibold text-gray-700 pb-1 pl-2">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in trancheOrder" :key="t" class="border-t border-gray-100">
              <td
                class="py-1.5 pr-2 whitespace-nowrap cursor-pointer select-none"
                :class="activeTranche === t ? 'text-blue-700 font-semibold' : 'text-gray-700 hover:text-gray-900'"
                @click="toggleTranche(t)"
              >
                <span class="inline-block w-2 h-2 rounded-sm mr-1.5 align-middle" :style="{ backgroundColor: trancheStyle(t).hex }" />{{ trancheShort(t) }}
              </td>
              <td
                v-for="c in matrixCategories"
                :key="c"
                class="py-1.5 px-1 text-right tabular-nums cursor-pointer rounded"
                :class="cellClass(t, c)"
                @click="selectCell(t, c)"
              >{{ matrixValue(t, c) ? matrixValue(t, c).toLocaleString() : '' }}</td>
              <td class="py-1 pl-2 text-right tabular-nums font-semibold text-gray-800">{{ (counts[t] || 0).toLocaleString() }}</td>
            </tr>
            <tr class="border-t border-gray-200">
              <td class="py-1 pr-2 font-semibold text-gray-700">Total</td>
              <td v-for="c in matrixCategories" :key="c" class="py-1 px-1.5 text-right tabular-nums font-semibold text-gray-700">
                {{ (categoryCounts[c] || 0).toLocaleString() }}
              </td>
              <td class="py-1 pl-2 text-right tabular-nums font-semibold text-gray-900">{{ grandTotal.toLocaleString() }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="flex-1 min-h-0 flex overflow-hidden rounded border border-gray-200 bg-white">
      <div class="flex-1 flex flex-col overflow-hidden" :class="selected ? 'w-1/2' : 'w-full'">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 gap-3">
          <div class="flex items-center gap-3">
            <h3 class="text-sm font-semibold text-gray-900">
              Risk Tranche
              <span v-if="loading" class="text-gray-400 font-normal ml-1">Loading…</span>
              <span v-else class="text-gray-400 font-normal ml-1">{{ total.toLocaleString() }} patients</span>
            </h3>
            <select
              v-model="activeCategory"
              class="text-xs border border-gray-200 rounded px-2 py-1 text-gray-700 bg-white"
            >
              <option value="">All intervention categories</option>
              <option v-for="(label, key) in categoryLabels" :key="key" :value="key">
                {{ label }}<template v-if="categoryCounts[key]"> ({{ categoryCounts[key] }})</template>
              </option>
            </select>
            <select
              v-if="contracts.length"
              v-model="activeContract"
              class="sensitive text-xs border border-gray-200 rounded px-2 py-1 text-gray-700 bg-white max-w-[15rem] truncate"
            >
              <option value="">All contracts</option>
              <option v-for="c in contracts" :key="c.contract || 'none'" :value="c.contract || ''" :disabled="!c.contract">
                {{ contractLabel(c) }} ({{ c.n.toLocaleString() }})
              </option>
            </select>
            <div v-if="Object.keys(horizonCounts).length" class="flex items-center gap-1">
              <button
                v-for="h in horizonOrder"
                :key="h"
                class="px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors"
                :class="activeHorizon === h ? 'border-rose-300 ' + horizonStyle(h) : 'border-gray-200 text-gray-500 hover:border-gray-300'"
                @click="toggleHorizon(h)"
              >{{ horizonLabel(h) }} <span class="text-gray-400">{{ (horizonCounts[h] || 0).toLocaleString() }}</span></button>
            </div>
            <button
              v-if="a1c.listed"
              class="px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors"
              :class="activeA1c ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-gray-200 text-gray-500 hover:border-gray-300'"
              :title="a1c.assessed.toLocaleString() + ' of the ' + a1c.listed.toLocaleString() + ' patients on IHCI\'s Advanced Illness Care list have an assessment'"
              @click="activeA1c = !activeA1c"
            >AIC list <span class="text-gray-400">{{ a1c.assessed.toLocaleString() }} / {{ a1c.listed.toLocaleString() }}</span></button>
          </div>
          <div class="flex items-center gap-2">
            <input
              v-model.trim="search" data-demo-search
              type="text"
              placeholder="Patient or physician"
              class="sensitive text-xs border border-gray-200 rounded px-2 py-1 w-44"
            />
            <ExportMenu :exports="[{ type: 'csv', label: 'Export CSV', action: exportCsv, loading: exporting }]" :disabled="loading || !total" size="sm" />
          </div>
        </div>

        <div class="flex-1 overflow-auto">
          <div v-if="loading" class="p-4">
            <div v-for="i in 12" :key="i" class="h-10 bg-gray-50 rounded mb-1 animate-pulse" />
          </div>

          <div v-else-if="!results.length" class="flex-1 flex items-center justify-center py-16">
            <p class="text-xs text-gray-400">No patients have been assessed yet</p>
          </div>

          <table v-else class="w-full text-xs">
            <thead class="sticky top-0 bg-gray-50 z-10">
              <tr>
                <th class="px-2 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap">Patient</th>
                <th class="px-2 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap">Physician / NPI</th>
                <th class="px-2 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap">Department</th>
                <th class="px-2 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap" title="Payer / contract / performance year from EMPI's latest attribution delivery">Contract</th>
                <th class="px-2 py-1.5 text-center font-medium text-gray-500 whitespace-nowrap" title="Open opportunities the patient is enrolled in">Enrolled</th>
                <th class="px-2 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap">Tranche</th>
                <th class="px-2 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap">Horizon</th>
                <th class="px-2 py-1.5 text-right font-medium text-gray-500 whitespace-nowrap" title="Risk score, 0 to 100, and its percentile within the tranche">Score</th>
                <th class="px-2 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap">Intervention Category</th>
                <th class="px-2 py-1.5 text-left font-medium text-gray-500">First Action</th>
                <th class="px-2 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap">Assessed</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in results"
                :key="r.row_key"
                class="border-t border-gray-100 cursor-pointer transition-colors"
                :class="isSelected(r) ? 'bg-blue-50' : 'hover:bg-gray-50'"
                @click="select(r)"
              >
                <td class="px-2 py-1.5 text-gray-700 whitespace-nowrap">
                  <div class="sensitive">
                    <span v-if="r.patient_name" class="sensitive" data-spoof-kind="person">{{ r.patient_name }}</span>
                    <span v-else class="font-mono sensitive" data-spoof-kind="identifier">{{ r.patient_identifier }}</span>
                  </div>
                  <span v-if="r.a1c" class="inline-block px-1.5 rounded bg-amber-50 text-amber-800 text-[10px] font-semibold" title="On IHCI's Advanced Illness Care list">AIC</span>
                </td>
                <td class="px-2 py-1.5 sensitive">
                  <div class="text-gray-700 truncate max-w-[150px] sensitive" data-spoof-kind="provider">{{ r.physician_name || '' }}</div>
                  <div class="font-mono text-[10px] text-gray-400 sensitive" data-spoof-kind="identifier">{{ r.physician_npi || '' }}</div>
                </td>
                <td class="px-2 py-1.5 text-gray-600 truncate max-w-[170px]" :title="r.department || ''">{{ r.department || '' }}</td>
                <td class="px-2 py-1.5 text-gray-600 whitespace-nowrap sensitive" :title="contractLabel(r)"><span class="sensitive" data-spoof-kind="contract">{{ r.contract || '' }}</span> <span v-if="r.performance_year" class="text-[10px] text-gray-400 sensitive">{{ r.performance_year }}</span></td>
                <td class="px-2 py-1.5 text-center">
                  <span
                    v-if="r.open_opportunities"
                    class="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold"
                    :title="r.open_opportunities + ' open ' + (r.open_opportunities === 1 ? 'opportunity' : 'opportunities')"
                  >{{ r.open_opportunities }}</span>
                  <span v-else class="text-gray-300">–</span>
                </td>
                <td class="px-2 py-1.5">
                  <span
                    class="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap"
                    :class="trancheStyle(r.risk_tranche).pill"
                  >{{ trancheShort(r.risk_tranche) }}</span>
                  <span v-if="r.risk_tranche_confidence" class="ml-1 text-[10px] text-gray-400">{{ r.risk_tranche_confidence }}</span>
                </td>
                <td class="px-2 py-1.5 whitespace-nowrap">
                  <span v-if="r.acute_horizon" class="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium" :class="horizonStyle(r.acute_horizon)">{{ horizonLabel(r.acute_horizon) }}</span>
                </td>
                <td class="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                  <template v-if="r.risk_score != null">
                    <span class="font-semibold text-gray-800">{{ r.risk_score }}</span>
                    <span v-if="r.risk_percentile != null" class="text-[10px] text-gray-400 ml-1">p{{ r.risk_percentile }}</span>
                  </template>
                  <span v-else class="text-gray-300">–</span>
                </td>
                <td class="px-2 py-1.5 text-gray-700 truncate max-w-[130px]" :title="categoryLabel(r.intervention_category)">{{ categoryLabel(r.intervention_category) }}</td>
                <td class="px-2 py-1.5 text-gray-500 max-w-[180px] truncate">{{ r.next_best_actions[0] && r.next_best_actions[0].action }}</td>
                <td class="px-2 py-1.5 text-gray-500 whitespace-nowrap">{{ formatDate(r.scored_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!loading && total > pageSize" class="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50">
          <span class="text-xs text-gray-500">
            {{ offset + 1 }}–{{ Math.min(offset + pageSize, total) }} of {{ total.toLocaleString() }}
          </span>
          <div class="flex gap-1">
            <button
              class="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
              :disabled="offset <= 0"
              @click="offset = Math.max(0, offset - pageSize)"
            >Prev</button>
            <button
              class="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
              :disabled="offset + pageSize >= total"
              @click="offset += pageSize"
            >Next</button>
          </div>
        </div>
      </div>

      <div v-if="selected" class="w-1/2 border-l border-gray-200 overflow-hidden flex flex-col bg-white">
        <Patient_Drawer
          :key="selected.patient_identifier"
          :patient-identifier="selected.patient_identifier"
          :entity-id="selected.entity_id"
          :patient-name="selected.patient_name || ''"
          :enabled-tabs="drawerTabs"
          :show-header="true"
          default-tab="insights"
          metric-category="Care Management"
          @close="selected = null"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue'
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue'
import Pie_Chart from '@/components/widgetcomponents/Viz_Elements/Pie_Chart.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import {
  TRANCHE_ORDER, TRANCHE_LABELS, TRANCHE_SHORT, TRANCHE_STYLES, TRANCHE_DESCRIPTIONS, CATEGORY_LABELS,
  HORIZON_ORDER, HORIZON_LABELS, HORIZON_STYLES,
} from '@/components/Care_Management/riskTranche'
import { beginSensitiveEgress } from '@/utils/demoEgress'
import demoProtectedFields from '@/mixins/demoProtectedFields'

export default {
  name: 'CareManagement_AIRiskScore',
  components: { Patient_Drawer, Worklist_FilterBar, Pie_Chart, ExportMenu },
  mixins: [demoProtectedFields],
  // The patient/physician search identifies real patients and providers. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['search'],
  data() {
    return {
      loading: true,
      counts: {},
      categoryCounts: {},
      horizonCounts: {},
      a1c: { assessed: 0, listed: 0 },
      contracts: [],
      matrix: {},
      total: 0,
      results: [],
      selected: null,
      activeTranche: '',
      activeCategory: '',
      activeHorizon: '',
      activeA1c: false,
      activeContract: '',
      search: '',
      entityFilters: null,
      drawerTabs: ['insights', 'details', 'utilization', 'programs'],
      offset: 0,
      pageSize: 100,
      trancheOrder: TRANCHE_ORDER,
      horizonOrder: HORIZON_ORDER,
      categoryLabels: CATEGORY_LABELS,
      searchTimer: null,
      exporting: false,
    }
  },
  computed: {
    pieData() {
      return {
        labels: TRANCHE_ORDER.map(t => TRANCHE_SHORT[t]),
        values: TRANCHE_ORDER.map(t => this.counts[t] || 0),
        colors: TRANCHE_ORDER.map(t => TRANCHE_STYLES[t].hex),
      }
    },
    pieKey() {
      return this.pieData.values.join('-')
    },
    pieConfig() {
      return {
        margin: { top: 8, right: 8, bottom: 8, left: 8 },
        innerRadius: 45,
        outerRadius: null,
        padAngle: 0.02,
        cornerRadius: 2,
        labelDisplay: { showLabels: false, showValues: false, externalLabels: false, minAngleForLabel: 0.05, minPercentageForLabel: 5, labelOffset: 10 },
        legend: { display: false, position: 'none', spacing: 0 },
        formatting: { percentage: true, sig_figs: 0 },
        title: '',
        subtitle: '',
        tooltip: { enabled: true },
      }
    },
    matrixCategories() {
      return Object.keys(CATEGORY_LABELS).filter(c => this.categoryCounts[c])
    },
    grandTotal() {
      return Object.values(this.counts).reduce((a, b) => a + b, 0)
    },
  },
  watch: {
    activeTranche() { this.offset = 0; this.fetchPage() },
    activeCategory() { this.offset = 0; this.fetchPage() },
    activeHorizon() { this.offset = 0; this.fetchPage() },
    activeA1c() { this.offset = 0; this.fetchPage() },
    activeContract() { this.offset = 0; this.fetchPage() },
    offset() { this.fetchPage() },
    search() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => { this.offset = 0; this.fetchPage() }, 300)
    },
  },
  mounted() {
    this.fetchPage()
  },
  methods: {
    async fetchPage() {
      this.loading = true
      try {
        const params = { ...this.listParams(), offset: this.offset, limit: this.pageSize }
        const { data } = await axios.get('/api/care-management/risk-tranche/', { params })
        this.counts = data.counts || {}
        this.categoryCounts = data.category_counts || {}
        this.horizonCounts = data.horizon_counts || {}
        this.a1c = data.a1c || { assessed: 0, listed: 0 }
        this.contracts = data.contracts || []
        this.matrix = data.matrix || {}
        this.total = data.total || 0
        this.results = data.results || []
      } finally {
        this.loading = false
      }
    },
    listParams() {
      const params = {}
      if (this.activeTranche) params.tranche = this.activeTranche
      if (this.activeCategory) params.category = this.activeCategory
      if (this.activeHorizon) params.horizon = this.activeHorizon
      if (this.activeA1c) params.a1c = 1
      if (this.activeContract) params.contract = this.activeContract
      if (this.search) params.search = this.search
      if (this.entityFilters) params.entity_filters = JSON.stringify(this.entityFilters)
      return params
    },
    async exportCsv() {
      const ticket = beginSensitiveEgress('export')
      if (!ticket) return
      this.exporting = true
      try {
        const { data } = await axios.get('/api/care-management/risk-tranche/export/', {
          params: this.listParams(),
          responseType: 'blob',
        })
        if (!ticket.confirm()) return
        const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }))
        const a = document.createElement('a')
        a.href = url
        a.download = `risk_stratification_${new Date().toISOString().slice(0, 10)}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } finally {
        this.exporting = false
      }
    },
    onEntityFilterChanged(filters) {
      this.entityFilters = filters.entity_filters || null
      this.offset = 0
      this.selected = null
      this.fetchPage()
    },
    toggleTranche(t) {
      this.activeTranche = this.activeTranche === t ? '' : t
    },
    toggleHorizon(h) {
      this.activeHorizon = this.activeHorizon === h ? '' : h
      if (this.activeHorizon) this.activeTranche = 'intervene_to_prevent_unplanned_acute'
    },
    horizonLabel(key) { return HORIZON_LABELS[key] || key },
    horizonStyle(key) { return HORIZON_STYLES[key] || '' },
    toggleCategory(c) {
      this.activeCategory = this.activeCategory === c ? '' : c
    },
    selectCell(t, c) {
      if (this.activeTranche === t && this.activeCategory === c) {
        this.activeTranche = ''
        this.activeCategory = ''
      } else {
        this.activeTranche = t
        this.activeCategory = c
      }
    },
    clearSelection() {
      this.activeTranche = ''
      this.activeCategory = ''
      this.activeHorizon = ''
      this.activeA1c = false
      this.activeContract = ''
    },
    contractLabel(c) {
      if (!c || !c.contract) return ''
      return [c.payer, c.contract, c.performance_year].filter(Boolean).join(' / ')
    },
    onSliceClick(slice) {
      this.toggleTranche(TRANCHE_ORDER[slice.index])
    },
    pct(n) {
      return this.grandTotal ? Math.round(100 * (n || 0) / this.grandTotal) + '%' : ''
    },
    matrixValue(t, c) {
      return (this.matrix[t] || {})[c] || 0
    },
    cellClass(t, c) {
      const selected = this.activeTranche === t && this.activeCategory === c
      if (selected) return 'bg-blue-100 text-blue-900 font-semibold'
      const rowOrCol = (this.activeTranche === t && !this.activeCategory) || (this.activeCategory === c && !this.activeTranche)
      if (rowOrCol) return 'bg-blue-50 text-gray-800'
      return this.matrixValue(t, c) ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300'
    },
    select(r) {
      this.selected = this.isSelected(r) ? null : r
    },
    isSelected(r) {
      return this.selected && this.selected.row_key === r.row_key
    },
    trancheLabel(key) { return TRANCHE_LABELS[key] || key },
    trancheShort(key) { return TRANCHE_SHORT[key] || key },
    trancheStyle(key) { return TRANCHE_STYLES[key] || TRANCHE_STYLES.no_intervention_necessary },
    trancheDescription(key) { return TRANCHE_DESCRIPTIONS[key] || '' },
    categoryLabel(key) { return CATEGORY_LABELS[key] || key || '' },
    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },
  },
}
</script>
